import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Heading,
  Icon,
  Skeleton,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/outline';
import { useCallback, useEffect, useRef } from 'react';
import { useTranslations } from 'use-intl';

import { noop } from '@blockpulse3/data/shared';
import { ErrorQueryCard } from '@blockpulse3/ui/commons';

import { useGetOnboardingExistingCompany } from '../../../hooks/useGetOnboardingExistingCompany';
import { useStepFormContext } from '../../../provider';
import { RepresentantInformationsForm, representantTitle } from './RepresentantInformationsForm';
import { representativeFormDefaults } from './schema';
import { getRepresentatives } from './utils';

type Props = {
  /* ** Is editing mode ** */
  isEditing?: boolean;
  /* ** Step cancelled callback ** */
  onCancel?: () => void;
  /* ** Step submitted callback ** */
  onSubmit?: () => void;
};

type IntlRole = keyof IntlMessages['RoleValues'];

/**
 * ExistingCompanyRepresentatStep.
 * Step that renders the form for the representant of the company.
 * It is splitted on purpose, to keep the form agnostic.
 *
 * @param {Props}
 * @returns {JSX.Element}
 */
export function CompanyRepresentatives({
  isEditing = false,
  onCancel = noop,
  onSubmit = noop,
}: Props): JSX.Element {
  /* ** Ref to externaly submit nested accordion forms (Legal,Natural RepresentantForm) ** */
  const formRef = useRef<HTMLFormElement>(null);

  const i18nRoleValues = useTranslations('RoleValues');
  const i18nRepresentantInformationsStep = useTranslations(
    'OnboardingExistingRepresentantInformationsStep',
  );

  const { data, loading, error, refetch } = useGetOnboardingExistingCompany(isEditing);

  const representatives = getRepresentatives(data?.company.companyRepresentative);
  const newForm = representatives.length === 0;

  const { setCancelHandler, setSubmitHandler } = useStepFormContext();
  const handleStepSubmit = useCallback((): void => {
    /*
     * If the user submit with the second button with modified data from a already fetched
     * representative, with the current design, we can do nothing for him
     *
     */
    if (newForm) {
      formRef.current?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
    } else {
      onSubmit();
    }
  }, [newForm, formRef, onSubmit]);

  const handleStepCancel = useCallback((): void => {
    onCancel();
  }, [onCancel]);

  useEffect(() => {
    setSubmitHandler(handleStepSubmit);
    setCancelHandler(handleStepCancel);
  }, [handleStepSubmit, handleStepCancel, setSubmitHandler, setCancelHandler]);

  const handleFormSubmit = (): void => {
    refetch();
  };

  if (loading) {
    return <Skeleton height="350px" width="full" />;
  }

  if (error || !data) {
    return <ErrorQueryCard width="full" />;
  }

  return (
    <>
      {representatives.map((representative) => {
        return (
          <Accordion key={representative.id} allowToggle>
            <AccordionItem mt="0">
              <AccordionButton>
                <Tooltip
                  hasArrow
                  label={i18nRepresentantInformationsStep('Tooltip.Success')}
                  placement="top"
                >
                  <Icon as={CheckCircleIcon} boxSize="30" color="green.500" />
                </Tooltip>
                <Box flex="1" p="2" textAlign="left">
                  <Heading fontSize="lg">{i18nRoleValues(representative.role as IntlRole)}</Heading>
                  <Text>{representantTitle(representative)}</Text>
                </Box>
                <AccordionIcon boxSize="8" />
              </AccordionButton>
              <AccordionPanel>
                <RepresentantInformationsForm
                  defaultValues={representative}
                  id={representative.id}
                  ref={formRef}
                  shouldCancel={false}
                  onSubmitComplete={handleFormSubmit}
                />
              </AccordionPanel>
            </AccordionItem>
          </Accordion>
        );
      })}
      {newForm && (
        <Accordion allowToggle>
          <AccordionItem mt="0">
            <AccordionButton>
              <Tooltip
                hasArrow
                label={i18nRepresentantInformationsStep('Tooltip.Warning')}
                placement="top"
              >
                <Icon as={ExclamationCircleIcon} boxSize="30" color="yellow.400" />
              </Tooltip>
              <Box flex="1" p="2" textAlign="left">
                <Heading fontSize="lg">{i18nRoleValues('General')}</Heading>
              </Box>
              <AccordionIcon boxSize="8" />
            </AccordionButton>
            <AccordionPanel>
              <RepresentantInformationsForm
                defaultValues={representativeFormDefaults}
                id={null}
                ref={formRef}
                shouldCancel={false}
                onSubmitComplete={handleFormSubmit}
              />
            </AccordionPanel>
          </AccordionItem>
        </Accordion>
      )}
    </>
  );
}

export type CompanyRepresentativesProps = Props;
